import React, { useState } from 'react';
import { artists } from '../mock/data';
import { Instagram, Music, Facebook, ChevronDown, ChevronUp } from 'lucide-react';
import { FaTiktok, FaSoundcloud } from 'react-icons/fa';

const Artists = () => {
  const [expandedBios, setExpandedBios] = useState({});

  const toggleBio = (artistId) => {
    setExpandedBios(prev => ({
      ...prev,
      [artistId]: !prev[artistId]
    }));
  };

  const getBioPreview = (bio) => {
    // Get first 3 sentences or ~200 characters
    const sentences = bio.split('. ');
    const preview = sentences.slice(0, 3).join('. ') + '.';
    return preview.length > 250 ? bio.substring(0, 250) + '...' : preview;
  };

  return (
    <section id="artists" className="relative py-24 bg-black">
      {/* Background effect */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-green-900/10 via-black to-black"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4">
            OUR
            <span className="block text-green-400 mt-2 drop-shadow-[0_0_20px_rgba(0,255,136,0.8)]">
              ARTISTS
            </span>
          </h2>
          <div className="w-24 h-1 bg-green-500 mx-auto mt-6 shadow-[0_0_10px_rgba(0,255,136,0.8)]"></div>
        </div>

        {/* Artists Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {artists.map((artist) => (
            <div 
              key={artist.id}
              className="group relative bg-zinc-900/50 backdrop-blur-sm border border-green-500/20 rounded-2xl overflow-hidden hover:border-green-500/60 transition-all duration-500 hover:shadow-[0_0_40px_rgba(0,255,136,0.3)]"
            >
              {/* Artist Image */}
              <div className="relative h-80 overflow-hidden bg-zinc-900">
                <img 
                  src={artist.image} 
                  alt={artist.name}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                
                {/* Hover overlay with glow */}
                <div className="absolute inset-0 bg-green-500/0 group-hover:bg-green-500/10 transition-colors duration-500"></div>
              </div>

              {/* Artist Info */}
              <div className="relative p-6 -mt-20">
                <div className="relative z-10">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2 group-hover:text-green-400 transition-colors duration-300">
                    {artist.name}
                  </h3>
                  
                  {/* Pure FM Badge - Only for Certified Dan */}
                  {artist.id === 1 && (
                    <div className="mb-4">
                      <div className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold px-6 py-3 rounded-lg shadow-lg animate-pulse">
                        <div className="text-base">🎵 NOW ON PURE FM</div>
                        <div className="text-sm font-normal mt-1">Tuesdays 7 - 9 PM</div>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-green-400 font-semibold mb-4 flex items-center gap-2">
                    <Music size={18} />
                    {artist.role}
                  </p>
                  <div className="text-gray-300 leading-relaxed mb-4">
                    <p>
                      {expandedBios[artist.id] ? artist.bio : getBioPreview(artist.bio)}
                    </p>
                    <button
                      onClick={() => toggleBio(artist.id)}
                      className="text-green-400 hover:text-green-300 text-sm font-semibold mt-3 flex items-center gap-1 transition-colors"
                    >
                      {expandedBios[artist.id] ? (
                        <>
                          Show Less <ChevronUp size={16} />
                        </>
                      ) : (
                        <>
                          Read More <ChevronDown size={16} />
                        </>
                      )}
                    </button>
                  </div>

                  {/* Submit Demo Button (only for Certified Dan) */}
                  {artist.id === 1 && (
                    <a 
                      href="#demo"
                      className="inline-block mb-6 px-6 py-3 bg-green-500 hover:bg-green-600 text-black font-bold rounded-lg transition-all duration-300 shadow-[0_0_20px_rgba(0,255,136,0.5)] hover:shadow-[0_0_30px_rgba(0,255,136,0.8)]"
                    >
                      Submit Your Demo
                    </a>
                  )}

                  {/* Live Show Info */}
                  {artist.liveShow && (
                    <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-green-400 font-semibold text-sm flex items-center gap-2">
                        <Music size={16} />
                        {artist.liveShow}
                      </p>
                      {artist.radioUrl && (
                        <a 
                          href={artist.radioUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-green-400 text-xs mt-2 inline-block transition-colors"
                        >
                          {artist.radioUrl.replace('https://', '')}
                        </a>
                      )}
                    </div>
                  )}

                  {/* Social Links */}
                  <div className="flex gap-4">
                    {artist.social.instagram && (
                      <a 
                        href={artist.social.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-zinc-800/50 rounded-lg hover:bg-green-500/20 border border-green-500/20 hover:border-green-500 transition-all duration-300 text-gray-400 hover:text-green-400"
                        aria-label="Instagram"
                      >
                        <Instagram size={20} />
                      </a>
                    )}
                    {artist.social.tiktok && (
                      <a 
                        href={artist.social.tiktok}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-zinc-800/50 rounded-lg hover:bg-green-500/20 border border-green-500/20 hover:border-green-500 transition-all duration-300 text-gray-400 hover:text-green-400"
                        aria-label="TikTok"
                      >
                        <FaTiktok size={20} />
                      </a>
                    )}
                    {artist.social.soundcloud && (
                      <a 
                        href={artist.social.soundcloud}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-zinc-800/50 rounded-lg hover:bg-green-500/20 border border-green-500/20 hover:border-green-500 transition-all duration-300 text-gray-400 hover:text-green-400"
                        aria-label="SoundCloud"
                      >
                        <FaSoundcloud size={20} />
                      </a>
                    )}
                    {artist.social.facebook && (
                      <a 
                        href={artist.social.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-zinc-800/50 rounded-lg hover:bg-green-500/20 border border-green-500/20 hover:border-green-500 transition-all duration-300 text-gray-400 hover:text-green-400"
                        aria-label="Facebook"
                      >
                        <Facebook size={20} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Artists;
